/** ER-11594 — map inspections-by-version API payload onto InspectionsTableByVersion. */

export const STATUS_LABEL = {
  not_started: "Not Started",
  in_progress: "In Progress",
  passed: "Passed",
  failed: "Failed",
  not_applicable: "Not Applicable",
};

export const OUTCOME_LABEL = {
  not_attempted: null,
  passed: "Passed",
  failed: "Failed",
  not_required: "Not Required",
};

export const LEARNING_CHIP_LABEL = {
  has_seen_learning: "Has Seen Learning",
  did_not_see_learning: "Did not See Learning",
};

export const OUTCOME_CHIP_LABEL = {
  not_attempted: "Not attempted",
  passed: "Passed",
  failed: "Failed",
  not_required: "Not required",
};

export const ALL_STATUS_VALUES = [
  "not_started",
  "in_progress",
  "passed",
  "failed",
  "not_applicable",
];

export const ALL_OUTCOME_VALUES = ["not_attempted", "passed", "failed", "not_required"];

export const DEFAULT_INSPECTION_FILTERS = {
  status: [...ALL_STATUS_VALUES],
  learning: "no_filter",
  quiz: "no_filter",
  inspection: "no_filter",
};

const LIST_FILTER_KEYS = ["status", "learning", "quiz", "inspection", "employee", "inspected_by"];

function asList(value) {
  if (value == null || value === "no_filter") return [];
  return Array.isArray(value) ? value : [value];
}

function isSameSet(list, expected) {
  if (!Array.isArray(list) || list.length !== expected.length) return false;
  return expected.every((entry) => list.includes(entry));
}

function addList(params, key, value) {
  if (value == null || value === "no_filter") return;
  const list = asList(value);
  if (!list.length) return;
  if (key === "status" && isSameSet(list, ALL_STATUS_VALUES)) return;
  if ((key === "quiz" || key === "inspection") && isSameSet(list, ALL_OUTCOME_VALUES)) return;
  params[key] = list;
}

export function toQueryParams(filters = {}, search) {
  const params = {};
  if (search?.trim()) params.search = search.trim();

  addList(params, "status", filters.status);
  addList(params, "learning", filters.learning);
  addList(params, "quiz", filters.quiz);
  addList(params, "inspection", filters.inspection);
  addList(params, "employee", filters.employee);
  addList(params, "inspected_by", filters.inspected_by);
  return params;
}

function chipLabelFor(key, value) {
  if (key === "status") return STATUS_LABEL[value] || value;
  if (key === "learning") return LEARNING_CHIP_LABEL[value] || value;
  if (key === "quiz" || key === "inspection") return OUTCOME_CHIP_LABEL[value] || value;
  return String(value);
}

export function toChipFilters(filters = {}) {
  const chips = {};
  LIST_FILTER_KEYS.forEach((key) => {
    const list = asList(filters[key]);
    if (!list.length) return;
    if (key === "status" && isSameSet(list, ALL_STATUS_VALUES)) return;
    if ((key === "quiz" || key === "inspection") && isSameSet(list, ALL_OUTCOME_VALUES)) return;
    chips[key] = list.map((value) => chipLabelFor(key, value)).join(", ");
  });
  return chips;
}

export function syncPayloadFromChips(previous = {}, nextChips = {}) {
  const next = {
    ...DEFAULT_INSPECTION_FILTERS,
    employee: previous.employee,
    inspected_by: previous.inspected_by,
  };
  LIST_FILTER_KEYS.forEach((key) => {
    if (nextChips[key] == null || String(nextChips[key]).trim() === "") {
      if (key === "status") next.status = [...ALL_STATUS_VALUES];
      else if (key === "employee" || key === "inspected_by") delete next[key];
      else next[key] = "no_filter";
      return;
    }
    if (previous[key] != null) next[key] = previous[key];
  });
  return next;
}

export function mapEmployee(person) {
  if (!person) return null;
  return {
    id: person.id,
    firstName: person.first_name,
    lastName: person.last_name,
    position: person.position,
    department: person.department,
    photoUrl: person.photo_url,
  };
}

function mapEnum(labels, value) {
  return Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : value;
}

/** Overall status badge date: terminal outcomes use inspection time, then quiz, then learning. */
function deriveStatusDate(row) {
  if (!row) return null;
  const status = row.status;
  if (status === "passed" || status === "failed" || status === "not_applicable") {
    return row.inspected_on ?? row.quiz_attempted_on ?? row.learning_seen_on ?? null;
  }
  if (status === "in_progress") {
    return row.quiz_attempted_on ?? row.learning_seen_on ?? null;
  }
  return null;
}

export function mapRow(row) {
  if (!row) return {};
  return {
    id: row.id,
    versionId: row.version_id,
    status: mapEnum(STATUS_LABEL, row.status),
    statusDate: deriveStatusDate(row),
    learningSeenDate: row.learning_seen_on,
    quizStatus: mapEnum(OUTCOME_LABEL, row.quiz_status),
    quizDate: row.quiz_attempted_on,
    inspectionStatus: mapEnum(OUTCOME_LABEL, row.inspection_status),
    inspectedOnDate: row.inspected_on,
    employee: mapEmployee(row.employee),
    inspectedBy: mapEmployee(row.inspected_by),
    chatterUnreadCount: row.chatter_unread_count,
    canReset: row.can_reset,
  };
}

export function mapInspectionsPayload(data = {}) {
  return {
    inspectionCount: data.count ?? 0,
    totalCount: data.total_count ?? 0,
    canBulkReset: Boolean(data.can_bulk_reset),
    resetGroup: (data.reset_group?.rows ?? []).map(mapRow),
    versionGroups: (data.version_groups ?? []).map((group) => ({
      id: group.id,
      versionLabel: group.version_label,
      publishedDate: group.published_at,
      isCurrent: group.is_current,
      configurationSummary: group.configuration_summary,
      rows: (group.rows ?? []).map(mapRow),
    })),
  };
}

export function inspectionFilterFieldError(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== "object") return "";
  if (typeof data.detail === "string") return data.detail;
  if (typeof data.message === "string") return data.message;
  const entry = Object.entries(data).find(([, messages]) => messages != null);
  if (!entry) return "";
  const [field, messages] = entry;
  const message = Array.isArray(messages) ? messages[0] : messages;
  return message ? `${field}: ${message}` : "";
}
